import { z } from 'zod'
import { paginationQuerySchema } from './api-response.ts'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { decimalFitsNumeric } from './units.ts'

export const productionWorkTaskListQuerySchema = paginationQuerySchema.extend({
  limit: z.coerce.number().int().positive().max(50).default(50),
  search: z.string().trim().max(200).optional(),
  equipmentId: uuidSchema.optional(),
  workCenterId: uuidSchema.optional(),
  view: z.enum(['mine', 'queue', 'completed']).default('mine'),
})
export type ProductionWorkTaskListQuery = z.infer<typeof productionWorkTaskListQuerySchema>

export const productionWorkAssignmentInputSchema = z
  .strictObject({
    equipmentId: uuidSchema.nullable().optional(),
    assigneeId: z.string().trim().min(1).max(200).nullable().optional(),
    priority: z.enum(['low', 'medium', 'high']).optional(),
    scheduledDate: z.iso.date().nullable().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, 'Передайте щонайменше одне поле')
export type ProductionWorkAssignmentInput = z.infer<typeof productionWorkAssignmentInputSchema>

export const productionWorkStartInputSchema = z.strictObject({ requestId: uuidSchema })
export type ProductionWorkStartInput = z.infer<typeof productionWorkStartInputSchema>
export const productionWorkPauseInputSchema = z.strictObject({
  requestId: uuidSchema,
  reason: z.string().trim().min(1).max(1000),
})
export type ProductionWorkPauseInput = z.infer<typeof productionWorkPauseInputSchema>

const outputQuantity = decimalSchema.refine(
  (value) => !value.startsWith('-') && decimalFitsNumeric(value, 18, 3),
  'Вкажіть невід’ємну кількість з точністю до трьох знаків після коми',
)
export const productionWorkOutputInputSchema = z
  .strictObject({
    requestId: uuidSchema,
    goodQuantity: outputQuantity,
    scrapQuantity: outputQuantity,
    scrapReason: z.string().trim().min(1).max(1000).nullable().optional().default(null),
    note: z.string().trim().min(1).max(1000).nullable().optional().default(null),
  })
  .refine(
    (input) => /[1-9]/.test(input.goodQuantity) || /[1-9]/.test(input.scrapQuantity),
    'Вкажіть додатну кількість виготовленого або браку',
  )
  .refine((input) => !/[1-9]/.test(input.scrapQuantity) || input.scrapReason !== null, {
    message: 'Вкажіть причину браку',
    path: ['scrapReason'],
  })
export type ProductionWorkOutputInput = z.infer<typeof productionWorkOutputInputSchema>

export const productionWorkTaskSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  orderKind: z.enum(['customer', 'internal']),
  /** Null for internal orders: their route belongs to the order, which has no item rows. */
  itemId: uuidSchema.nullable(),
  itemName: z.string(),
  operationName: z.string(),
  workCenterId: uuidSchema.nullable(),
  workCenterName: z.string().nullable(),
  equipmentId: uuidSchema.nullable(),
  equipmentName: z.string().nullable(),
  assigneeId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  priority: z.enum(['low', 'medium', 'high']),
  scheduledDate: z.iso.date().nullable(),
  deadline: timestampSchema.nullable(),
  plannedQuantity: decimalSchema.nullable(),
  unitSymbol: z.string().nullable(),
  goodQuantity: decimalSchema,
  scrapQuantity: decimalSchema,
  workedSeconds: z.number().int().nonnegative(),
  runningSince: timestampSchema.nullable(),
  sessionWorkerId: z.string().nullable(),
  state: z.enum(['waiting', 'ready', 'running', 'paused', 'done']),
  blockReason: z.string().nullable(),
})
export type ProductionWorkTask = z.infer<typeof productionWorkTaskSchema>

export const productionWorkSessionSchema = z.object({
  id: uuidSchema,
  stageId: uuidSchema,
  workerId: z.string(),
  workerName: z.string(),
  equipmentId: uuidSchema.nullable(),
  equipmentName: z.string().nullable(),
  startedAt: timestampSchema,
  endedAt: timestampSchema.nullable(),
  pauseReason: z.string().nullable(),
  endKind: z.enum(['pause', 'complete']).nullable(),
})
export type ProductionWorkSession = z.infer<typeof productionWorkSessionSchema>

export const productionWorkResultSchema = z.object({
  id: uuidSchema,
  requestId: uuidSchema,
  stageId: uuidSchema,
  sessionId: uuidSchema,
  goodQuantity: decimalSchema,
  scrapQuantity: decimalSchema,
  scrapReason: z.string().nullable(),
  note: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: timestampSchema,
})
export type ProductionWorkResult = z.infer<typeof productionWorkResultSchema>

export const productionWorkTaskDetailSchema = z.object({
  task: productionWorkTaskSchema,
  sessions: z.array(productionWorkSessionSchema),
  results: z.array(productionWorkResultSchema),
})
export type ProductionWorkTaskDetail = z.infer<typeof productionWorkTaskDetailSchema>

export const productionWorkOperatorSchema = z.object({ id: z.string(), name: z.string() })
export type ProductionWorkOperator = z.infer<typeof productionWorkOperatorSchema>
